// Uploaded documents are sent to the backend as base64 data URLs inside the
// form JSON, so a few raw phone photos (4–8 MB each) quickly exceed what the
// reverse proxy accepts. Photos are therefore downscaled and re-encoded as JPEG
// before being read, which keeps them legible while dividing their size by ~10.

// Longest side, in pixels, an uploaded image is scaled down to.
export const MAX_IMAGE_SIDE = 2000
const JPEG_QUALITY = 0.85

// GIFs may be animated and SVGs are vectors: re-encoding would lose both.
const SKIPPED_IMAGE_TYPES = ['image/gif', 'image/svg+xml']

export function fitWithin(width: number, height: number, maxSide: number): { width: number; height: number } {
	const scale = Math.min(1, maxSide / Math.max(width, height))
	return { width: Math.round(width * scale), height: Math.round(height * scale) }
}

function toJpeg(canvas: HTMLCanvasElement): Promise<Blob | null> {
	return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY))
}

// Returns a smaller JPEG version of the image, or the file itself when it is
// not a compressible image, the browser cannot decode it (e.g. HEIC outside
// Safari), or re-encoding would not make it smaller.
export async function compressImage(file: File): Promise<Blob> {
	if (!file.type.startsWith('image/') || SKIPPED_IMAGE_TYPES.includes(file.type)) return file
	if (typeof createImageBitmap !== 'function') return file

	let bitmap: ImageBitmap
	try {
		// Browsers apply the EXIF orientation by default, so portrait phone
		// photos stay upright once drawn.
		bitmap = await createImageBitmap(file)
	} catch {
		return file
	}

	try {
		const { width, height } = fitWithin(bitmap.width, bitmap.height, MAX_IMAGE_SIDE)
		const canvas = document.createElement('canvas')
		canvas.width = width
		canvas.height = height
		const ctx = canvas.getContext('2d')
		if (!ctx) return file
		// JPEG has no alpha channel: without a background, transparent areas
		// would turn black.
		ctx.fillStyle = '#ffffff'
		ctx.fillRect(0, 0, width, height)
		ctx.drawImage(bitmap, 0, 0, width, height)

		const jpeg = await toJpeg(canvas)
		return jpeg && jpeg.size < file.size ? jpeg : file
	} finally {
		bitmap.close()
	}
}
